import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, getFirestore, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext';
import app from '../../firebase/firebaseConfig';
import AddUserModal from './AddUserModal'; // Import the new modal

const AdminDashboard = () => {
    const db = getFirestore(app);
    const functions = getFunctions(app); // Initialize Firebase Functions
    const { currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // --- NEW: State for the modal ---
    const [isAddUserModalOpen, setAddUserModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- NEW: State and logic for displaying Farm ID ---
    const [farmDetails, setFarmDetails] = useState({ name: 'Loading...', id: currentUser.farmId });
    const [copySuccess, setCopySuccess] = useState('');

    useEffect(() => {
        if (!currentUser.farmId) return;

        // Fetch farm details for the display card
        const fetchFarmDetails = async () => {
            const farmRef = doc(db, 'farms', currentUser.farmId);
            const farmSnap = await getDoc(farmRef);
            if (farmSnap.exists()) {
                setFarmDetails({ id: farmSnap.id, ...farmSnap.data() });
            }
        };
        fetchFarmDetails();

        // Fetch the list of users for the management table
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('farmId', '==', currentUser.farmId));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const userList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUsers(userList);
            setIsLoading(false);
        }, (err) => {
            console.error(err);
            setError('Failed to load users.');
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser.farmId, db]);

    const handleRoleChange = async (userId, newRole) => {
        if (userId === currentUser.uid) {
            setError("You cannot change your own role.");
            return;
        }
        setError('');
        setSuccess('');
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, { role: newRole });
            setSuccess(`Successfully updated role for user.`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(err);
            setError('Failed to update role.');
        }
    };

    const handleAddNewUser = async (newUserData) => {
        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const createNewUser = httpsCallable(functions, 'createNewUser');
            const result = await createNewUser({
                email: newUserData.email,
                password: newUserData.password,
                role: newUserData.role,
                farmId: currentUser.farmId
            });

            if (result.data.success) {
                setSuccess('User created successfully!');
                setAddUserModalOpen(false);
            } else {
                throw new Error(result.data.error || 'Failed to create user.');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // --- NEW: Handler for copying Farm ID ---
    const handleCopyId = () => {
        navigator.clipboard.writeText(currentUser.farmId).then(() => {
            setCopySuccess('Copied!');
            setTimeout(() => setCopySuccess(''), 2000);
        }, () => {
            setCopySuccess('Failed to copy');
            setTimeout(() => setCopySuccess(''), 2000);
        });
    };

    if (isLoading) {
        return <p>Loading users...</p>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
                <button 
                    onClick={() => setAddUserModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed"
                    disabled
                >
                    + Add New User
                </button>
            </div>
            
            {/* --- NEW: Farm ID Display Card --- */}
            <div className="mb-6 bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-r-lg">
                <h3 className="text-lg font-semibold text-indigo-800">{farmDetails.name}</h3>
                <p className="text-sm text-gray-600 mt-1">Share this ID with new users to have them join your farm:</p>
                <div className="mt-2 flex items-center bg-white p-2 rounded-md shadow-sm">
                    <input type="text" readOnly value={farmDetails.id} className="flex-grow p-1 border-none bg-transparent focus:ring-0 text-sm text-gray-700" />
                    <button onClick={handleCopyId} className="ml-2 px-3 py-1 bg-indigo-500 text-white text-xs font-bold rounded-md hover:bg-indigo-600">
                        {copySuccess || 'Copy ID'}
                    </button>
                </div>
            </div>

            <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">
                <p className="font-bold">Free Plan Notice</p>
                <p>The "Add New User" function is not available on the free plan. But users can join with the Farm ID as new. Please upgrade to a paid plan to support this feature.</p>
            </div>
            {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
            {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map(user => (
                            <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.role}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {user.id !== currentUser.uid ? (
                                        <select 
                                            defaultValue={user.role} 
                                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                            className="form-select rounded-md shadow-sm"
                                        >
                                            <option value="admin">Admin</option>
                                            <option value="manager">Manager</option>
                                            <option value="worker">Worker</option>
                                        </select>
                                    ) : (
                                        <span className="text-xs text-gray-400">Cannot change own role</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <AddUserModal 
                isOpen={isAddUserModalOpen} 
                onClose={() => setAddUserModalOpen(false)} 
                onSubmit={handleAddNewUser} 
                isLoading={isSubmitting} 
            />
        </div>
    );
};

export default AdminDashboard;
